import {
  Alert,
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { useParams } from 'react-router-dom';
import { API_ENDPOINT } from '../../configurations/global.config';
import { GenericResponseType } from '../../interfaces/common/common';
import { OrderType } from '../../interfaces/delivery-company/orders';
import { generateStateTrace } from './delivery-tracker.utils';
import { IconWithText } from '../common/styles/components';
import {
  Check,
  CheckCircleOutline,
  CheckOutlined,
  LocalShipping,
  LocationOn,
  Pending,
  Person2,
  Store,
} from '@mui/icons-material';
import AddressWithIcon from '../common/styles/address-with-icon';
import { displayLocation } from '../../utils/locations';
import QRCodeBox from '../common/qrcode-box/qrcode-box';
import { encodePermanentPackage } from '../../utils/encoding';
import { DeliveryStateTrackingName } from '../../enums/orders';
import { APP_VERSION_HEADER } from '../../utils/app_authentication';
import { Stepper, Step, StepLabel } from '@mui/material';

const maxLineLength = 20;

const DeliveryTracker: React.FC = () => {
  const { hash } = useParams();

  const [order, setOrder] = useState<OrderType>();
  const [loading, setLoading] = useState<boolean>(false);
  const [response, setResponse] = useState<GenericResponseType>({
    message: '',
  });
  const [imageSrc, setImageSrc] = useState('');

  const isMobileView = isMobile && !isTablet;

  const handleError = () => {
    // Fallback to a default image when the original fails to load
    setImageSrc(
      `https://parcel-tracer-public.s3.eu-central-1.amazonaws.com/system/logo.png?v=${new Date().getTime()}`
    );
  };

  useEffect(() => {
    const getOrder = async () => {
      setLoading(true);

      if (hash === undefined) {
        setResponse({ statusCode: 400, message: 'Invalid tracking link' });
        setLoading(false);
        return;
      }

      const endpoint = `${API_ENDPOINT}/api/order-tracker/${encodeURI(hash)}`;

      try {
        const resp = await axios.get<OrderType>(endpoint, {
          headers: APP_VERSION_HEADER,
        });

        setOrder(resp.data);
        setImageSrc(
          `https://parcel-tracer-public.s3.eu-central-1.amazonaws.com/${resp.data.organisation?.organisation_id}/logo.png?v=${new Date().getTime()}`
        );
      } catch (e) {
        setResponse({ statusCode: 500, message: 'Failed to fetch order data' });
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    getOrder();
  }, [hash]);

  const splitText = (text: string): string[] => {
    if (!text) return [];
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      if (currentLine.length + word.length <= maxLineLength) {
        currentLine += (currentLine.length > 0 ? ' ' : '') + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }

    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    return lines;
  };

  const steps = useMemo(
    () => (order?.statestamps ? generateStateTrace(order.statestamps) : []),
    [order?.statestamps]
  );

  const activeStepIndex = useMemo(() => {
    // Completed steps are those with a timestamp (in our generator, done == timestamp present)
    if (!steps.length) return 0;
    const completedCount = steps.filter((s) => !!s.timestamp).length;
    return completedCount;
  }, [steps]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: (theme) =>
          theme.palette.mode === 'light'
            ? 'grey.100'
            : theme.palette.background.default,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        py: { xs: 2, md: 4 },
        px: { xs: 1, md: 2 },
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={4}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '70vh',
          }}
        >
          {/* Top bar / loading indicator */}
          {loading && <LinearProgress />}

          <Box
            sx={{
              p: { xs: 2, md: 3 },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 2, md: 3 },
            }}
          >
            {/* Header / logo & title */}
            <Stack
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{
                textAlign: 'center',
              }}
            >
              {imageSrc && (
                <Box
                  component="img"
                  sx={{
                    maxHeight: { xs: 60, md: 80 },
                    maxWidth: { xs: 180, md: 220 },
                    objectFit: 'contain',
                  }}
                  src={imageSrc}
                  onError={handleError}
                  alt="Organisation Logo"
                />
              )}

              <Typography
                variant={isMobileView ? 'h6' : 'h5'}
                component="h1"
                sx={{ fontWeight: 700 }}
              >
                {order ? `Order #${order.order_id}` : 'Order Tracking'}
              </Typography>
            </Stack>

            {/* Error state */}
            {!loading && !order && response.message && (
              <Alert severity="error" variant="outlined">
                {response.message}
              </Alert>
            )}

            {/* Main content */}
            {!loading && order && (
              <>
                {/* QR + summary */}
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    mb: { xs: 2, md: 3 },
                  }}
                >
                  <CardContent>
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mb: { xs: 2, sm: 0 },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <QRCodeBox
                            value={encodePermanentPackage(order, 1)}
                            centered
                            size={isMobileView ? 120 : 140}
                            showValue
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={8}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{ textTransform: 'uppercase', color: 'text.secondary' }}
                            >
                              Delivery Agency
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {order.organisation?.organisation_id ?? 'N/A'}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{ textTransform: 'uppercase', color: 'text.secondary' }}
                            >
                              # of Packages
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {order.number_of_packages}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{ textTransform: 'uppercase', color: 'text.secondary' }}
                            >
                              Customer
                            </Typography>
                            <Typography variant="body1">
                              {order.customer?.name ?? 'N/A'}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{ textTransform: 'uppercase', color: 'text.secondary' }}
                            >
                              Merchant
                            </Typography>
                            <Typography variant="body1">
                              {order.merchant?.name ?? 'N/A'}
                            </Typography>
                            {order.merchant_location?.contact_phone && (
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                              >
                                {order.merchant_location.contact_phone}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Details section */}
                <Grid container spacing={2} mb={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
                      <CardContent>
                        <IconWithText centered={!isMobileView}>
                          <Person2 color="primary" />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Customer
                          </Typography>
                        </IconWithText>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {order.customer?.name ?? 'N/A'}
                        </Typography>

                        <IconWithText centered={!isMobileView}>
                          <LocationOn color="primary" />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Customer Address
                          </Typography>
                        </IconWithText>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2">
                          {displayLocation(order.customer_location)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
                      <CardContent>
                        <IconWithText centered={!isMobileView}>
                          <Store color="primary" />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Merchant
                          </Typography>
                        </IconWithText>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {order.merchant?.name ?? 'N/A'}
                        </Typography>
                        {order.merchant_location?.contact_phone && (
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', mb: 1 }}
                          >
                            {order.merchant_location.contact_phone}
                          </Typography>
                        )}

                        <Typography
                          variant="subtitle2"
                          sx={{ mt: 2, fontWeight: 600 }}
                        >
                          Description
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        {splitText(order.description).map((line, idx) => (
                          <Typography
                            key={`${line}-${idx}`}
                            variant="body2"
                            sx={{ wordBreak: 'break-word' }}
                          >
                            {line}
                          </Typography>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Delivery Status */}
                <Box
                  sx={{
                    borderRadius: 3,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    p: { xs: 2, md: 3 },
                  }}
                >
                  <Typography
                    variant={isMobileView ? 'h6' : 'h5'}
                    component="h2"
                    sx={{
                      mb: { xs: 2, md: 3 },
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: 'primary.main',
                    }}
                  >
                    Delivery Status
                  </Typography>

                  {steps.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ textAlign: 'center', color: 'text.secondary' }}
                    >
                      No status updates available yet.
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        overflowX: isMobileView ? 'auto' : 'visible',
                      }}
                    >
                      <Stepper
                        activeStep={activeStepIndex}
                        orientation={isMobileView ? 'vertical' : 'horizontal'}
                        alternativeLabel={!isMobileView}
                        sx={{
                          width: isMobileView ? '100%' : 'auto',
                          '& .MuiStepLabel-label': {
                            whiteSpace: isMobileView ? 'normal' : 'nowrap',
                          },
                        }}
                      >
                        {steps.map((step, index) => (
                          <Step key={`${step.label}-${index}`}>
                            <StepLabel
                              icon={step.icon}
                              StepIconComponent={() => step.icon}
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{
                                  color: '#333',
                                  fontSize: isMobileView ? '0.9rem' : '1rem',
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1
                                }}
                              >
                                {
                                  DeliveryStateTrackingName[
                                    step.label as keyof typeof DeliveryStateTrackingName
                                  ]
                                }
                                {
                                  step.done ? <CheckCircleOutline color={"success"} /> : <Pending color={"disabled"} />
                                }
                              </Typography>

                              {step.timestamp && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {new Date(step.timestamp).toLocaleString(
                                    undefined,
                                    {
                                      dateStyle: 'medium',
                                      timeStyle: 'short',
                                    }
                                  )}
                                </Typography>
                              )}

                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                  mt: 0.5,
                                }}
                              >
                                {step.helperText}
                              </Typography>
                            </StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </Box>
                  )}
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    mt: { xs: 2, md: 3 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.7rem',
                    }}
                  >
                    Powered by
                  </Typography>

                  <Box
                    component="img"
                    src="/main-logo.png"
                    alt="Powered by logo"
                    sx={{
                      maxHeight: { xs: 40, md: 50 },
                      maxWidth: { xs: 120, md: 160 },
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DeliveryTracker;
